import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  BugReport as BugIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Report as CriticalIcon,
  CheckCircle as EnabledIcon,
  Cancel as DisabledIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import errorLogger from '../../services/errorLogger.service';

interface DebugSetting {
  component: string;
  enabled: boolean;
}

interface ErrorLog {
  id: number;
  component: string;
  level: string;
  message: string;
  details?: any;
  created_at: string;
  user_name?: string;
  customer_name?: string;
}

const DebugSettings: React.FC = () => {
  const { member } = useAuth();
  const [settings, setSettings] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorLogs, setErrorLogs] = useState<ErrorLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);

  // Create logger for this component
  const logger = errorLogger.createComponentLogger('DebugSettings');

  // Check if user is master admin
  const isMasterAdmin = member?.memberType === 'master_admin' || (member as any)?.userType === 'master_admin';

  useEffect(() => {
    if (isMasterAdmin) {
      fetchSettings();
      fetchRecentLogs();
    }
  }, [isMasterAdmin]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/endpoints/debug-settings');
      if (response.data.success) {
        setSettings(response.data.settings || {});
      }
    } catch (err) {
      logger.error('Failed to fetch debug settings', err);
      setError('Failed to load debug settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentLogs = async (component?: string) => {
    try {
      setLogsLoading(true);
      const params: any = { limit: 20 };
      if (component) {
        params.error_type = component;
      }
      const response = await api.get('/endpoints/error-logs', { params });
      if (response.data.success) {
        setErrorLogs(response.data.errors || []);
      }
    } catch (err) {
      logger.error('Failed to fetch error logs', err, false);
    } finally {
      setLogsLoading(false);
    }
  };

  const toggleSetting = async (component: string, enabled: boolean) => {
    try {
      setSaving(component);
      setError(null);
      setSuccess(null);

      const response = await api.post('/endpoints/debug-settings', {
        component,
        enabled
      });

      if (response.data.success) {
        setSettings(prev => ({ ...prev, [component]: enabled }));
        setSuccess(`${component} logging ${enabled ? 'enabled' : 'disabled'}`);

        // Update local errorLogger settings if it's the current component
        if (component === 'DebugSettings') {
          await errorLogger.updateComponentSetting(component, enabled);
        }
      }
    } catch (err) {
      logger.error('Failed to update debug setting', err);
      setError('Failed to update setting');
      // Revert the change
      setSettings(prev => ({ ...prev, [component]: !enabled }));
    } finally {
      setSaving(null);
      // Clear success message after 3 seconds
      if (success) {
        setTimeout(() => setSuccess(null), 3000);
      }
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'debug':
      case 'info':
        return <InfoIcon fontSize="small" color="info" />;
      case 'warning':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'error':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'critical':
        return <CriticalIcon fontSize="small" color="error" />;
      default:
        return <BugIcon fontSize="small" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'debug':
        return 'default';
      case 'info':
        return 'info';
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  // Filter settings based on search
  const filteredSettings = Object.entries(settings).filter(([component]) =>
    component.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isMasterAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Access denied. This page is only available to master administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Debug Settings
        </Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            fetchSettings();
            fetchRecentLogs(selectedComponent || undefined);
          }}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Component Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Component Logging Settings
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enable or disable debug logging for specific components. Errors are always logged.
              </Typography>

              <TextField
                fullWidth
                placeholder="Search components..."
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Component</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center">Logging</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSettings.map(([component, enabled]) => (
                        <TableRow key={component}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {component}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              icon={enabled ? <EnabledIcon /> : <DisabledIcon />}
                              label={enabled ? 'Enabled' : 'Disabled'}
                              color={enabled ? 'success' : 'default'}
                              variant={enabled ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Switch
                              checked={enabled}
                              onChange={(e) => toggleSetting(component, e.target.checked)}
                              disabled={saving === component}
                              color="primary"
                              size="small"
                            />
                            {saving === component && (
                              <CircularProgress size={16} sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Error Logs */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Error Logs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Most recent log entries from all components
              </Typography>

              {logsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : errorLogs.length === 0 ? (
                <Alert severity="info">No recent error logs</Alert>
              ) : (
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {errorLogs.map((log) => (
                    <Box
                      key={log.id}
                      sx={{
                        p: 1.5,
                        mb: 1,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        '&:hover': {
                          bgcolor: 'action.hover',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        {getLevelIcon(log.level)}
                        <Chip
                          size="small"
                          label={log.level.toUpperCase()}
                          color={getLevelColor(log.level) as any}
                          sx={{ ml: 1, mr: 1 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {log.component}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          {new Date(log.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {log.message}
                      </Typography>
                      {log.user_name && (
                        <Typography variant="caption" color="text.secondary">
                          User: {log.user_name}
                        </Typography>
                      )}
                      {log.customer_name && (
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                          Customer: {log.customer_name}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Environment
              </Typography>
              <Typography variant="body1">
                {process.env.NODE_ENV === 'development' ? 'Development' : 'Production'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Console Output
              </Typography>
              <Typography variant="body1">
                {process.env.NODE_ENV === 'development' ? 'Enabled (Dev Only)' : 'Disabled'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" color="text.secondary">
                Error Display
              </Typography>
              <Typography variant="body1">
                Generic Messages Only
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DebugSettings;